<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import { useMapGetter } from 'dashboard/composables/store';
import mcpAuthorizationsAPI from 'dashboard/api/mcpAuthorizations';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t, tm, rt } = useI18n();

const serverUrl = `${window.location.origin}/mcp`;

const guides = computed(() =>
  ['CHATGPT', 'GEMINI', 'CLAUDE'].map(key => ({
    key,
    name: t(`INTEGRATION_SETTINGS.MCP.GUIDES.${key}.NAME`),
    steps: tm(`INTEGRATION_SETTINGS.MCP.GUIDES.${key}.STEPS`).map(step =>
      rt(step)
    ),
  }))
);

const accountId = useMapGetter('getCurrentAccountId');
const connectedApps = ref([]);
const appToRevoke = ref(null);
const revokeDialog = ref(null);

const { dateOnly } = useLocalizedTime();
const formatDate = date => dateOnly(new Date(date).getTime() / 1000);

const fetchConnectedApps = async () => {
  try {
    const { data } = await mcpAuthorizationsAPI.list(accountId.value);
    connectedApps.value = data;
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.ERROR'));
  }
};

const openRevokeDialog = app => {
  appToRevoke.value = app;
  revokeDialog.value.open();
};

const revokeApp = async () => {
  try {
    await mcpAuthorizationsAPI.revoke(accountId.value, appToRevoke.value.id);
    useAlert(t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKED'));
    await fetchConnectedApps();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.ERROR'));
  }
};

onMounted(fetchConnectedApps);

const copyServerUrl = async () => {
  await navigator.clipboard.writeText(serverUrl);
  useAlert(t('INTEGRATION_SETTINGS.MCP.SERVER_URL.COPIED'));
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.MCP.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.MCP.DESCRIPTION')"
        feature-name="integrations"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <section
          class="flex flex-col gap-3 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h3 class="text-heading-1 text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.MCP.SERVER_URL.TITLE') }}
          </h3>
          <p class="text-body-main text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.MCP.SERVER_URL.DESCRIPTION') }}
          </p>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 px-3 py-2 text-sm truncate rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              {{ serverUrl }}
            </code>
            <Button
              :label="$t('INTEGRATION_SETTINGS.MCP.SERVER_URL.COPY')"
              icon="i-lucide-copy"
              faded
              blue
              @click="copyServerUrl"
            />
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="text-heading-1 text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.MCP.GUIDES.TITLE') }}
          </h3>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div
              v-for="guide in guides"
              :key="guide.key"
              class="flex flex-col gap-3 p-4 outline outline-n-container outline-1 bg-n-card rounded-xl"
            >
              <h4 class="text-heading-3 text-n-slate-12">{{ guide.name }}</h4>
              <ol
                class="flex flex-col gap-2 pl-5 list-decimal text-body-main text-n-slate-11"
              >
                <li v-for="step in guide.steps" :key="step">{{ step }}</li>
              </ol>
            </div>
          </div>
        </section>

        <section
          class="flex flex-col gap-3 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h3 class="text-heading-1 text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.TITLE') }}
          </h3>
          <p
            v-if="!connectedApps.length"
            class="text-body-main text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.EMPTY') }}
          </p>
          <ul v-else class="flex flex-col divide-y divide-n-weak">
            <li
              v-for="app in connectedApps"
              :key="app.id"
              class="flex items-center justify-between gap-4 py-3"
            >
              <div class="flex flex-col min-w-0">
                <span class="text-heading-3 text-n-slate-12 truncate">
                  {{ app.client_name }} · {{ app.user_name }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{
                    $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.CONNECTED_AT', {
                      date: formatDate(app.created_at),
                    })
                  }}
                  ·
                  {{
                    app.last_used_at
                      ? $t(
                          'INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.LAST_USED',
                          { date: formatDate(app.last_used_at) }
                        )
                      : $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.NEVER_USED')
                  }}
                </span>
              </div>
              <Button
                :label="$t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKE')"
                faded
                ruby
                @click="openRevokeDialog(app)"
              />
            </li>
          </ul>
        </section>
        <Dialog
          ref="revokeDialog"
          type="alert"
          :title="$t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKE_TITLE')"
          :description="
            $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKE_MESSAGE', {
              name: appToRevoke?.client_name,
            })
          "
          :confirm-button-label="
            $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKE_YES')
          "
          :cancel-button-label="
            $t('INTEGRATION_SETTINGS.MCP.CONNECTED_APPS.REVOKE_NO')
          "
          @confirm="revokeApp"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
